import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { format, parseISO } from 'date-fns';

const SalesChart = ({ data, period }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return;

    // Initialize chart
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    // Prepare data
    const dates = data.map(item => {
      if (period === 'daily') {
        return format(parseISO(item.date), 'MMM dd');
      } else if (period === 'weekly') {
        return `Week ${item.week}`;
      } else if (period === 'yearly') {
        return item.year ? item.year.toString() : format(parseISO(item.date), 'yyyy');
      } else {
        return format(parseISO(item.date), 'MMM yyyy');
      }
    });

    const revenues = data.map(item => parseFloat(item.revenue || 0));
    const invoices = data.map(item => parseInt(item.invoices || 0));

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985'
          }
        },
        formatter: (params) => {
          let result = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`;
          params.forEach(param => {
            const value = param.seriesName === 'Revenue' 
              ? `QR ${param.value.toLocaleString()}`
              : `${param.value} invoices`;
            result += `
              <div style="display: flex; align-items: center; margin-bottom: 4px;">
                <span style="display: inline-block; width: 12px; height: 12px; background: ${param.color}; border-radius: 50%; margin-right: 8px;"></span>
                <span style="flex: 1;">${param.seriesName}:</span>
                <span style="font-weight: 600; margin-left: 16px;">${value}</span>
              </div>
            `;
          });
          return result;
        }
      },
      legend: {
        data: ['Revenue', 'Invoices'],
        top: 10,
        textStyle: {
          color: '#374151'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true
      },
      xAxis: [
        {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLine: {
            lineStyle: {
              color: '#e5e7eb'
            }
          },
          axisLabel: {
            color: '#6b7280',
            fontSize: 12
          }
        }
      ],
      yAxis: [
        {
          type: 'value',
          name: 'Revenue (QR)',
          position: 'left',
          axisLine: {
            lineStyle: {
              color: '#e5e7eb'
            }
          },
          axisLabel: {
            color: '#6b7280',
            fontSize: 12,
            formatter: (value) => {
              if (period === 'yearly' && value >= 1000000) {
                return `${(value / 1000000).toFixed(1)}M`;
              } else if (value >= 1000) {
                return `${(value / 1000).toFixed(0)}K`;
              }
              return value.toString();
            }
          },
          splitLine: {
            lineStyle: {
              color: '#f3f4f6'
            }
          }
        },
        {
          type: 'value',
          name: 'Invoices',
          position: 'right',
          axisLine: {
            lineStyle: {
              color: '#e5e7eb'
            }
          },
          axisLabel: {
            color: '#6b7280',
            fontSize: 12
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: [
        {
          name: 'Revenue',
          type: 'line',
          smooth: true,
          data: revenues,
          lineStyle: {
            width: 3,
            color: '#dc2626'
          },
          itemStyle: {
            color: '#dc2626',
            borderWidth: 2,
            borderColor: '#ffffff'
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(220, 38, 38, 0.3)' },
                { offset: 1, color: 'rgba(220, 38, 38, 0.05)' }
              ]
            }
          },
          emphasis: {
            focus: 'series'
          }
        },
        {
          name: 'Invoices',
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          data: invoices,
          lineStyle: {
            width: 3,
            color: '#2563eb'
          },
          itemStyle: {
            color: '#2563eb',
            borderWidth: 2,
            borderColor: '#ffffff'
          },
          emphasis: {
            focus: 'series'
          }
        }
      ]
    };

    chartInstance.current.setOption(option, true);

    // Handle resize
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [data, period]);

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  return (
    <div 
      ref={chartRef} 
      className="w-full h-full"
      style={{ minHeight: '300px' }}
    />
  );
};

export default SalesChart;